import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, FILE_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import IdeaCard from "@/components/IdeaCard";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function TryCritiqueDetail() {
  const { id } = useParams();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/try-and-critique/${id}`),
      api.get(`/try-and-critique/${id}/reviews`),
    ])
      .then(([p, r]) => { setProduct(p.data); setReviews(r.data); })
      .catch(() => toast("Could not load product."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, user?.user_id]);

  const submit = async () => {
    if (!user) return login();
    setBusy(true);
    try {
      const { data } = await api.post(`/try-and-critique/${id}/request`, { message });
      toast(data.already ? "You've already requested this." : "Request sent. The site owner will follow up.");
      setMessage("");
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not send request.");
    } finally { setBusy(false); }
  };

  if (loading || !product) return <div className="max-w-6xl mx-auto px-6 pt-20 text-[#2A2823]/60">Loading…</div>;

  const my = product.my_request;
  const canReview = my && my.status === "fulfilled";

  return (
    <main className="max-w-6xl mx-auto px-6 pt-10 pb-24">
      <Link to="/try-and-critique" className="accent-label text-[#B79256] hover:text-[#4E5A3D]">← back to Try &amp; Critique</Link>

      <div className="mt-6 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-6">
          <div className="rounded-[2rem] overflow-hidden border border-[#B79256]/25 bg-white">
            <img src={FILE_URL(product.photo_path)} alt={product.product_name} className="w-full max-h-[75vh] object-cover"/>
          </div>
        </div>
        <div className="md:col-span-6">
          <div className="accent-label text-[#B79256]">{product.brand_name} · {product.category}</div>
          <h1 data-testid="tc-product-title" className="font-serif-display text-5xl sm:text-6xl text-[#2A2823] mt-3 leading-[1.05]">{product.product_name}</h1>
          {product.website && (
            <a href={product.website} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-sm text-[#4E5A3D] underline underline-offset-2">
              {new URL(product.website).host}
            </a>
          )}
          <p className="mt-6 text-lg text-[#2A2823]/85 leading-relaxed whitespace-pre-wrap">{product.description}</p>

          <div className="mt-6 rounded-2xl border border-[#B79256]/30 bg-white/60 p-4 text-sm text-[#2A2823]/75 flex gap-3">
            <ShieldCheck className="w-5 h-5 text-[#4E5A3D] flex-shrink-0 mt-0.5" strokeWidth={1.75}/>
            <div>
              A brand paid a flat fee to be reviewed here — clearly labeled as a Try &amp; Critique branch. Every review below is honestly written by a Willoah user, labeled <b>"Gifted to me"</b>, and treated identically to any organic branch. Reviews cannot be bought, removed, or rewritten by the brand.
            </div>
          </div>

          {/* Request / status card */}
          <div className="mt-6 rounded-2xl border border-[#4E5A3D]/25 bg-[#4E5A3D]/6 p-5">
            {!user ? (
              <>
                <p className="font-serif-display text-2xl text-[#2A2823]">Sign in to request this product</p>
                <Button data-testid="tc-signin-btn" onClick={login} className="mt-4 rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-11 px-6 shadow-none">
                  Sign in with Google
                </Button>
              </>
            ) : my ? (
              <>
                <div className="accent-label text-[#4E5A3D]">your request</div>
                <p className="font-serif-display text-2xl text-[#2A2823] mt-1" data-testid="tc-my-status">
                  {my.status === "requested" && "Requested — the site owner will follow up."}
                  {my.status === "fulfilled" && "Fulfilled — ready when you are."}
                  {my.status === "declined" && "Declined this time."}
                </p>
                {my.status === "fulfilled" && (
                  <Button
                    data-testid="tc-plant-review-btn"
                    onClick={() => navigate(`/plant?brand_product_id=${product.id}`)}
                    className="mt-4 rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-11 px-6 shadow-none"
                  >
                    Plant your honest review
                  </Button>
                )}
              </>
            ) : (
              <>
                <p className="font-serif-display text-2xl text-[#2A2823]">Ask to try this</p>
                <Textarea
                  data-testid="tc-request-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Optional — a sentence about why you're a fit."
                  rows={3}
                  className="mt-3 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] focus-visible:ring-[#4E5A3D]"
                />
                <Button
                  data-testid="tc-request-btn"
                  onClick={submit}
                  disabled={busy}
                  className="mt-3 rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-11 px-6 shadow-none"
                >
                  Send request
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <section className="mt-16">
        <div className="flex items-baseline justify-between">
          <h2 className="font-serif-display text-3xl text-[#2A2823]">Honest branches on this product</h2>
          <span className="accent-label text-[#2A2823]/50">{reviews.length} total</span>
        </div>
        <p className="text-sm text-[#2A2823]/60 mt-2">All labeled "Gifted to me" — never boosted, never buried.</p>
        {reviews.length === 0 ? (
          <p data-testid="tc-no-reviews" className="mt-6 text-[#2A2823]/50 italic">No reviews yet. Recipients: your Field Note will be the first honest signal.</p>
        ) : (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.map((idea, i) => <IdeaCard key={idea.id} idea={idea} index={i} />)}
          </div>
        )}
      </section>
    </main>
  );
}
